import axios from 'axios';
import { API_BASE_URL } from '../config/api';

// Updated API URL to use centralized config

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 0, // No timeout - let requests complete naturally
});

// Add request interceptor for debugging
api.interceptors.request.use(
  (config) => {
    const fullUrl = `${config.baseURL}${config.url}`;
    console.log('🚀 API Request:', config.method?.toUpperCase(), fullUrl);
    console.log('📦 Request data:', config.data);
    console.log('🔑 Auth header:', config.headers.Authorization ? 'Present' : 'Missing');
    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor for debugging
api.interceptors.response.use(
  (response) => {
    console.log('API Response:', response.status, response.data);
    return response;
  },
  (error) => {
    console.error('API Response Error:', error.response?.status, error.response?.data || error.message);
    return Promise.reject(error);
  }
);

// Request interceptor for adding auth tokens
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export const apiService = {
  // Health check
  health: () => api.get('/health'),

  // Wedding endpoints
  weddings: {
    getAll: () => api.get('/weddings'),
    getById: (id) => api.get(`/weddings/${id}`),
    create: (weddingData) => api.post('/weddings', weddingData),
    update: (id, weddingData) => api.put(`/weddings/${id}`, weddingData),
    delete: (id) => api.delete(`/weddings/${id}`),
  },

  // User endpoints
  users: {
    register: (userData) => api.post('/auth/register', userData),
    login: (credentials) => api.post('/auth/login', credentials),
    getProfile: () => api.get('/auth/profile'),
    updateProfile: (userData) => api.put('/auth/profile', userData),
  },

  // Dream Generator endpoints
  dream: {
    test: () => api.get('/dream/test'),
    generateTheme: (dreamData) => {
      // Send as JSON with optional model parameter
      return api.post('/dream/generate-dream', {
        text: dreamData.text,
        model: dreamData.model // 'claude', 'openai', 'both', or undefined for auto
      });
    },
    compareModels: (dreamData) => {
      // Generate themes with both models for comparison
      return api.post('/dream/compare-models', {
        text: dreamData.text
      });
    },
    save: (dreamData) => api.post('/dream/save', dreamData),
    getAll: () => api.get('/dream'),
    getById: (id) => api.get(`/dream/${id}`),
    generateSmartSuggestions: (suggestionData) => api.post('/dream/generate-smart-suggestions', suggestionData),
  },

  // Saved Themes API
  savedThemes: {
    save: (themeData) => api.post('/saved-themes', themeData),
    getAll: (params = {}) => api.get('/saved-themes', { params }),
    toggleFavorite: (id) => api.put(`/saved-themes/${id}/favorite`),
    delete: (id) => api.delete(`/saved-themes/${id}`)
  },

  // Budget endpoints
  budget: {
    create: (budgetData) => api.post('/budget/create', budgetData),
    getAll: () => api.get('/budget'),
    update: (id, budgetData) => api.put(`/budget/${id}`, budgetData),
    delete: (id) => api.delete(`/budget/${id}`),
    addExpense: (budgetId, expenseData) => api.post(`/budget/${budgetId}/expenses`, expenseData),
    updateExpense: (budgetId, expenseId, expenseData) => api.put(`/budget/${budgetId}/expenses/${expenseId}`, expenseData),
    deleteExpense: (budgetId, expenseId) => api.delete(`/budget/${budgetId}/expenses/${expenseId}`),
    // Budget Allocator methods
    getCategories: () => api.get('/budget/categories'),
    allocate: (allocationData) => api.post('/budget/allocate', allocationData),
    // Budget Management methods
    saveBudget: (budgetData) => api.post('/budget/save', budgetData),
    getSavedBudget: (userId) => api.get(`/budget/saved/${userId}`),
    deleteBudget: (budgetId) => api.delete(`/budget/${budgetId}`),
  },

  // Invitation endpoints
  invitations: {
    generate: (invitationData) => api.post('/invites/generate', invitationData),
    generatePDF: (invitationData) => api.post('/invites/generate-pdf', invitationData, { responseType: 'blob' }),
    getTemplates: () => api.get('/invites/templates'),
    save: (invitationData) => api.post('/invites/save', invitationData),
    getMyInvitations: () => api.get('/invites/my-invitations'),
    get: (uuid) => api.get(`/invites/${uuid}`),
    update: (uuid, invitationData) => api.put(`/invites/${uuid}`, invitationData),
    delete: (uuid) => api.delete(`/invites/${uuid}`),
    toggleFavorite: (uuid) => api.patch(`/invites/${uuid}/favorite`),
  },

  // Moodboard endpoints
  moodboard: {
    analyze: async (formData) => {
      // Use native fetch instead of axios to avoid any JSON serialization
      const token = localStorage.getItem('token');
      const headers = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API_BASE_URL}/moodboard/analyze`, {
        method: 'POST',
        headers: headers,
        body: formData // Don't set Content-Type - let browser handle it
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Request failed' }));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }
      
      return { data: await response.json() };
    },
    compareModels: async (formData) => {
      // Compare moodboard analysis with both AI models
      const token = localStorage.getItem('token');
      const headers = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API_BASE_URL}/moodboard/compare-models`, {
        method: 'POST',
        headers: headers,
        body: formData
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Request failed' }));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }
      
      return { data: await response.json() };
    },
    save: (moodboardData) => api.post('/moodboard/save', moodboardData),
    getAll: () => api.get('/moodboard'),
    getById: (id) => api.get(`/moodboard/${id}`),
    delete: (id) => api.delete(`/moodboard/${id}`),
  },

  // Vendor endpoints
  vendors: {
    search: (searchData) => api.post('/vendors/search', searchData),
    compare: (compareData) => api.post('/vendors/compare', compareData),
    getCategories: () => api.get('/vendors/categories'),
    getById: (id) => api.get(`/vendors/${id}`),
  },

  // Timeline Generator endpoints
  timeline: {
    generate: (timelineData) => api.post('/timeline/generate', timelineData),
    generatePDF: (timelineData) => {
      return api.post('/timeline/generate-pdf', timelineData, {
        responseType: 'blob',
      });
    },
  },

  // User Timelines endpoints (CRUD)
  userTimelines: {
    save: (timelineData) => api.post('/timeline/save', timelineData),
    getAll: () => api.get('/timeline/user'),
    getById: (id) => api.get(`/timeline/${id}`),
    update: (id, data) => api.put(`/timeline/${id}`, data),
    delete: (id) => api.delete(`/timeline/${id}`),
    toggleFavorite: (id) => api.patch(`/timeline/${id}/favorite`),
    generatePDF: (id) => api.post(`/timeline/${id}/pdf`, {}, { responseType: 'blob' }),
    orderEvents: (data) => api.post('/timeline/order-events', data)
  },

  // Weather Risk endpoints
  weather: {
    test: async () => {
      try {
        console.log('Testing weather API connection...');
        return await api.get('/weather/test');
      } catch (error) {
        console.error('Weather test API error:', error);
        throw error;
      }
    },
    assessRisk: async (weatherData) => {
      try {
        console.log('API Service - Sending weather forecast request:', weatherData);
        return await api.post('/weather/forecast', {
          location: weatherData.location_name,
          date: weatherData.wedding_date
        });
      } catch (error) {
        console.error('Weather forecast API error:', error);
        throw error;
      }
    },
    getCurrent: async (latitude, longitude, locationName) => {
      try {
        return await api.get('/weather/current', { 
          params: { latitude, longitude, location_name: locationName } 
        });
      } catch (error) {
        console.error('Current weather API error:', error);
        throw error;
      }
    },
    saveForecast: async (forecastData) => {
      try {
        console.log('API Service - Saving weather forecast:', forecastData);
        return await api.post('/weather/save', forecastData);
      } catch (error) {
        console.error('Save weather forecast API error:', error);
        throw error;
      }
    },
    getSavedForecast: async () => {
      try {
        return await api.get('/weather/my-forecast');
      } catch (error) {
        console.error('Get saved forecast API error:', error);
        throw error;
      }
    },
  },

  // Thank-you Notes endpoints
  thanks: {
    test: async () => {
      try {
        return await api.get('/thanks/test');
      } catch (error) {
        console.error('Thanks test API error:', error);
        throw error;
      }
    },
    generate: async (formData) => {
      try {
        console.log('API Service - Generating thank-you notes');
        return await api.post('/thanks/generate', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
      } catch (error) {
        console.error('Thanks generation API error:', error);
        throw error;
      }
    },
    getSampleCSV: async () => {
      try {
        return await api.get('/thanks/sample-csv', {
          responseType: 'blob',
        });
      } catch (error) {
        console.error('Sample CSV API error:', error);
        throw error;
      }
    },
  },

  // Memory endpoints
  memory: {
    test: async () => {
      try {
        return await api.get('/memory/test');
      } catch (error) {
        console.error('Memory test API error:', error);
        throw error;
      }
    },
    create: async (formData) => {
      try {
        console.log('API Service - Creating memory collection');
        return await api.post('/memory/create', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
      } catch (error) {
        console.error('Memory creation API error:', error);
        throw error;
      }
    },
    getById: async (id) => {
      try {
        return await api.get(`/memory/${id}`);
      } catch (error) {
        console.error('Memory retrieval API error:', error);
        throw error;
      }
    },
    getImageUrl: (filename) => {
      return `${API_BASE_URL}/memory/images/${filename}`;
    },
  },

  // Rituals endpoints
  rituals: {
    test: async () => {
      try {
        return await api.get('/rituals/test');
      } catch (error) {
        console.error('Rituals test API error:', error);
        throw error;
      }
    },
    getAllCultures: async () => {
      try {
        return await api.get('/rituals/cultures');
      } catch (error) {
        console.error('Get cultures API error:', error);
        throw error;
      }
    },
    getRitualsByCulture: async (culture) => {
      try {
        return await api.get(`/rituals/culture/${culture}`);
      } catch (error) {
        console.error('Get rituals by culture API error:', error);
        throw error;
      }
    },
    searchRituals: async (query, culture = null) => {
      try {
        const params = { query };
        if (culture) params.culture = culture;
        return await api.get('/rituals/search', { params });
      } catch (error) {
        console.error('Search rituals API error:', error);
        throw error;
      }
    },
    explainRituals: async (ritualsData) => {
      try {
        console.log('API Service - Explaining rituals');
        return await api.post('/rituals/explain', ritualsData);
      } catch (error) {
        console.error('Explain rituals API error:', error);
        throw error;
      }
    },
  },

  // Rituals endpoints
  rituals: {
    getCultures: async () => {
      try {
        const response = await api.get('/rituals/cultures');
        return response.data;
      } catch (error) {
        console.error('Get cultures API error:', error);
        throw error;
      }
    },
    getByCulture: async (culture) => {
      try {
        const response = await api.get(`/rituals/culture/${culture}`);
        return response.data;
      } catch (error) {
        console.error('Get rituals by culture API error:', error);
        throw error;
      }
    },
    getAll: async () => {
      try {
        const response = await api.get('/rituals/all');
        return response.data;
      } catch (error) {
        console.error('Get all rituals API error:', error);
        throw error;
      }
    },
    search: async (params) => {
      try {
        const response = await api.get('/rituals/search', { params });
        return response.data;
      } catch (error) {
        console.error('Search rituals API error:', error);
        throw error;
      }
    },
  },

  // User Rituals endpoints
  userRituals: {
    save: async (ritualId, notes = '') => {
      try {
        const response = await api.post('/user-rituals', {
          ritual_id: ritualId,
          notes
        });
        return response.data;
      } catch (error) {
        console.error('Save ritual API error:', error);
        throw error;
      }
    },
    getAll: async () => {
      try {
        const response = await api.get('/user-rituals');
        return response.data;
      } catch (error) {
        console.error('Get user rituals API error:', error);
        throw error;
      }
    },
    delete: async (id) => {
      try {
        const response = await api.delete(`/user-rituals/${id}`);
        return response.data;
      } catch (error) {
        console.error('Delete ritual API error:', error);
        throw error;
      }
    },
    toggleFavorite: async (id) => {
      try {
        const response = await api.patch(`/user-rituals/${id}/favorite`);
        return response.data;
      } catch (error) {
        console.error('Toggle favorite API error:', error);
        throw error;
      }
    },
    updateNotes: async (id, notes) => {
      try {
        const response = await api.patch(`/user-rituals/${id}/notes`, { notes });
        return response.data;
      } catch (error) {
        console.error('Update notes API error:', error);
        throw error;
      }
    },
  },

  // Wedding Events endpoints
  weddingEvents: {
    getAll: async () => {
      try {
        const response = await api.get('/wedding-events/all');
        return response.data;
      } catch (error) {
        console.error('Get wedding events API error:', error);
        throw error;
      }
    },
    getByCategory: async (category) => {
      try {
        const response = await api.get(`/wedding-events/category/${category}`);
        return response.data;
      } catch (error) {
        console.error('Get events by category API error:', error);
        throw error;
      }
    },
  },

  // Feedback endpoints
  feedback: {
    submitRating: (feedbackData) => api.post('/feedback/rating', feedbackData),
    submitThumbs: (feedbackData) => api.post('/feedback/thumbs', feedbackData),
    trackGenerated: (feedbackData) => api.post('/feedback/generated', feedbackData),
    trackSaved: (feedbackData) => api.post('/feedback/saved', feedbackData),
    trackVendorNavigation: (feedbackData) => api.post('/feedback/vendor-navigation', feedbackData),
    trackRitualNavigation: (feedbackData) => api.post('/feedback/ritual-navigation', feedbackData),
    trackTimeSpent: (feedbackData) => api.post('/feedback/time-spent', feedbackData),
    trackVendorInteraction: (feedbackData) => api.post('/feedback/vendor-interaction', feedbackData),
    trackRitualConversion: (feedbackData) => api.post('/feedback/ritual-conversion', feedbackData),
    trackRitualInteraction: (feedbackData) => api.post('/feedback/ritual-interaction', feedbackData),
    getDashboard: (dateRange) => api.get(`/feedback/analytics/dashboard?dateRange=${dateRange}`),
  },

  // A/B Testing endpoints
  abTesting: {
    getActiveExperiments: () => api.get('/ab-testing/experiments/active'),
    getProgress: (experimentKey) => api.get(`/ab-testing/experiments/${experimentKey}/progress`),
    getSignificance: (experimentKey, metric) => api.get(`/ab-testing/experiments/${experimentKey}/significance?metric=${metric}`),
    getEffectSize: (experimentKey, metric) => api.get(`/ab-testing/experiments/${experimentKey}/effect-size?metric=${metric}`),
    getConfidenceIntervals: (experimentKey, metric) => api.get(`/ab-testing/experiments/${experimentKey}/confidence-intervals?metric=${metric}`),
    getMonitoring: (experimentKey) => api.get(`/ab-testing/experiments/${experimentKey}/monitor`),
  },

  // Chat endpoints
  chat: {
    test: async () => {
      try {
        return await api.get('/chat/test');
      } catch (error) {
        console.error('Chat test API error:', error);
        throw error;
      }
    },
    getLanguages: async () => {
      try {
        return await api.get('/chat/languages');
      } catch (error) {
        console.error('Get languages API error:', error);
        throw error;
      }
    },
    getTones: async () => {
      try {
        return await api.get('/chat/tones');
      } catch (error) {
        console.error('Get tones API error:', error);
        throw error;
      }
    },
    getGreeting: async (language = 'en', tone = 'friendly') => {
      try {
        return await api.get('/chat/greeting', {
          params: { language, tone }
        });
      } catch (error) {
        console.error('Get greeting API error:', error);
        throw error;
      }
    },
    sendMessage: async (chatData) => {
      try {
        console.log('API Service - Sending chat message');
        return await api.post('/chat/chat', chatData);
      } catch (error) {
        console.error('Send chat message API error:', error);
        throw error;
      }
    },
  },
};
